import { ReactNode, CSSProperties } from "react";

import { styled } from "styled-components";

import {
    BackgroundFieldValue,
    cssColorsFromBackgroundField,
    radius as radii,
    scrollMargin,
    spacing,
    containerWidths
} from "../../styles";

export const Container = styled.div`
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    width: 100%;
    max-width: ${containerWidths.xl}px;
`;

type RowProps = React.HTMLAttributes<HTMLDivElement> & {
    verticalAlign?: string;
    align?: "left" | "right" | "center";
    style?: CSSProperties;
};

const InternalRow = styled.div<{
    $alignItems: CSSProperties["alignItems"];
    $justifyContent: CSSProperties["justifyContent"];
}>`
    align-items: ${({ $alignItems }) => $alignItems};
    justify-content: ${({ $justifyContent }) => $justifyContent};
    display: flex;
    flex-wrap: wrap;
    margin-left: -1rem;
    margin-right: -1rem;
`;

export const Row = ({
    verticalAlign = "stretch",
    align = "left",
    ...props
}: RowProps) => {
    let alignItems: CSSProperties["alignItems"] = "stretch";
    switch (verticalAlign.toLowerCase()) {
        case "top":
            alignItems = "flex-start";
            break;
        case "bottom":
            alignItems = "flex-end";
            break;
        case "center":
            alignItems = "center";
            break;
    }

    let justifyContent: CSSProperties["justifyContent"] = "flex-start";
    switch (align.toLowerCase()) {
        case "center":
            justifyContent = "center";
            break;
        case "right":
            justifyContent = "flex-end";
            break;
    }

    return (
        <InternalRow
            $alignItems={alignItems}
            $justifyContent={justifyContent}
            {...props}
        />
    );
};

export type BreakpointProperties = {
    span?: string;
    align?: string;
    order?: string;
    offset?: string;
    push?: string;
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    radius?: string;
};

type ColumnProperties = {
    xs?: BreakpointProperties;
    sm?: BreakpointProperties;
    md?: BreakpointProperties;
    lg?: BreakpointProperties;
    xl?: BreakpointProperties;
    backgroundField?: BackgroundFieldValue;
};

export function isColumnProperties(
    properties: unknown
): properties is ColumnProperties {
    return !!properties; // TODO: check properties is actually UiColumnProperties;
}

//up to 5 decimals in percentage widths for webkit (eg 7 => 58.333333%)
function spanWidth(span: string): string {
    const regex = RegExp(/^-?\d+(?:\.\d{0,5})?/);
    const percentage = regex.exec(((parseInt(span) * 100) / 12).toString());
    return percentage ? `${percentage[0]}%` : "auto";
}

function getColumnStyles({
    align,
    offset,
    push,
    order,
    radius,
    span,
    ...directions
}: BreakpointProperties): CSSProperties {
    const styles: CSSProperties = {};

    if (align) {
        styles.textAlign = align as CSSProperties["textAlign"];
    }
    if (offset) {
        styles.marginLeft = spanWidth(offset);
    }
    if (push) {
        styles.left = spanWidth(push);
    }
    if (order) {
        styles.order = parseInt(order);
    }
    if (span) {
        if (span === "0") {
            styles.display = "none";
        } else {
            styles.display = "block";
            styles.flex = "0 0 auto";
            styles.width = spanWidth(span);
        }
    }

    ["top", "bottom", "left", "right"].forEach((direction) => {
        const space = directions[direction as keyof typeof directions];
        if (space) {
            const marginKey = `margin${
                direction.charAt(0).toUpperCase() + direction.slice(1)
            }` as keyof CSSProperties;
            const paddingKey = `padding${
                direction.charAt(0).toUpperCase() + direction.slice(1)
            }` as keyof CSSProperties;

            if (space.startsWith("-")) {
                // z-index added to keep negative-margin div on top
                (styles as Record<string, unknown>)[marginKey] = `-${
                    spacing[space.substring(1)]
                }`;
                (styles as Record<string, unknown>)[paddingKey] = "1rem";
                styles.zIndex = 1;
            } else {
                (styles as Record<string, unknown>)[marginKey] = "0";
                (styles as Record<string, unknown>)[paddingKey] =
                    spacing[space];
            }
        }
    });

    if (radius) {
        styles.borderRadius = radii[radius as keyof typeof radii];
    }

    return styles;
}

// Helper to generate media query styles for a breakpoint
function generateBreakpointStyles(
    breakpoint: BreakpointProperties | undefined,
    minWidth?: string
): string {
    if (!breakpoint) return "";

    const styles = getColumnStyles(breakpoint);
    let cssString = "";

    Object.entries(styles).forEach(([key, value]) => {
        const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        cssString += `${cssKey}: ${value};`;
    });

    if (!minWidth) {
        return cssString;
    }

    return `
        @media (min-width: ${minWidth}) {
            ${cssString}
        }
    `;
}

// Styled component with responsive breakpoint support using transient props
const StyledColumnDiv = styled.div<{
    $backgroundField?: BackgroundFieldValue;
    $xs?: BreakpointProperties;
    $sm?: BreakpointProperties;
    $md?: BreakpointProperties;
    $lg?: BreakpointProperties;
    $xl?: BreakpointProperties;
}>`
    padding: 1rem;
    position: relative;
    scroll-margin-top: ${scrollMargin.top}px;
    text-align: center;
    width: 100%;

    ${({ $backgroundField }) =>
        $backgroundField ? cssColorsFromBackgroundField($backgroundField) : ""}
    ${({ $xs }) => ($xs ? generateBreakpointStyles($xs) : "")}
    ${({ $sm }) => ($sm ? generateBreakpointStyles($sm, "576px") : "")}
    ${({ $md }) => ($md ? generateBreakpointStyles($md, "768px") : "")}
    ${({ $lg }) => ($lg ? generateBreakpointStyles($lg, "992px") : "")}
    ${({ $xl }) => ($xl ? generateBreakpointStyles($xl, "1200px") : "")}
`;

export const Column = ({
    children,
    className: classesFromProps = "",
    xs,
    sm,
    md,
    lg,
    xl,
    backgroundField,
    ...props
}: {
    children?: ReactNode;
    xs?: BreakpointProperties;
    sm?: BreakpointProperties;
    md?: BreakpointProperties;
    lg?: BreakpointProperties;
    xl?: BreakpointProperties;
    backgroundField?: BackgroundFieldValue;
} & React.HTMLAttributes<HTMLDivElement>) => {
    // Build className for text alignment classes
    let classNames = classesFromProps;
    for (const [key, value] of Object.entries({ xs, sm, md, lg, xl })) {
        if (!!value && value?.align?.length) {
            classNames += ` ${key}-text-${value.align}`;
        }
    }
    classNames = classNames.toLowerCase();

    return (
        <StyledColumnDiv
            className={classNames}
            $backgroundField={backgroundField}
            $xs={xs}
            $sm={sm}
            $md={md}
            $lg={lg}
            $xl={xl}
            {...props}
        >
            {children}
        </StyledColumnDiv>
    );
};
